import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { TextField, InputAdornment, Switch, IconButton, Tooltip, Button, Box, Stack } from '@mui/material';
import { getApiKeys, createApiKey, updateApiKey, deleteApiKey } from '../../apis/apiKeys';
import { toast } from 'react-toastify';
import Loader from '../loader/Loader';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Add, ContentCopy, DeleteForeverOutlined } from '@mui/icons-material';
import { first, isEmpty } from 'lodash';
import TablePagination from '../TablePagination';
import { formatDate } from '../../lib/utils';

const validateIp = (ip) => {
    // Simple IPv4 validation
    return /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ip);
};

const APIKeys = () => {
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [newHost, setNewHost] = useState('');
    const [newName, setNewName] = useState('');
    const [viewKeyId, setViewKeyId] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [apiError, setApiError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortModel, setSortModel] = useState(null);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });

    const fetchKeys = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getApiKeys(sortModel);
            setApiKeys(data || []);
        } catch (err) {
            setError('Failed to load API keys');
            setApiKeys([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchKeys();
    }, [sortModel]);

    const handleAddKey = async () => {
        const errors = {};
        setApiError(null);
        if (!newName) errors.name = 'Name is required.';
        if (!newHost) errors.host = 'Host is required.';
        else if (!validateIp(newHost)) errors.host = 'Host must be a valid IP address.';
        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;
        try {
            await createApiKey({ host: newHost, name: newName });
            toast.success('API key created');
            setShowModal(false);
            setNewHost('');
            setNewName('');
            setFormErrors({});
            setApiError(null);
            fetchKeys();
        } catch (err) {
            // Try to parse API error response
            let apiErrMsg = 'Failed to create API key';
            if (typeof err === 'object' && err !== null) {
                if (err.host && Array.isArray(err.host)) {
                    apiErrMsg = err.host.join(' ');
                    setFormErrors((prev) => ({ ...prev, host: apiErrMsg }));
                } else if (err.name && Array.isArray(err.name)) {
                    apiErrMsg = err.name.join(' ');
                    setFormErrors((prev) => ({ ...prev, name: apiErrMsg }));
                } else {
                    apiErrMsg = JSON.stringify(err);
                }
            } else if (typeof err === 'string') {
                apiErrMsg = err;
            }
            setApiError(apiErrMsg);
            toast.error(apiErrMsg);
        }
    };

    const handleDeleteKey = async (id) => {
        if (window.confirm('Are you sure you want to delete this API key permanently?')) {
            try {
                await deleteApiKey(id);
                toast.success('API key deleted');
                fetchKeys();
            } catch (err) {
                toast.error('Failed to delete API key');
            }
        }
    };

    const handleCopy = (key) => {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard
                .writeText(key)
                .then(() => toast.success('Copied to clipboard'))
                .catch(() => toast.error('Failed to copy'));
        } else {
            // Fallback for unsupported browsers
            try {
                const textarea = document.createElement('textarea');
                textarea.value = key;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                toast.success('Copied to clipboard');
            } catch {
                toast.error('Clipboard not supported');
            }
        }
    };

    const handleView = (id) => {
        if (window.confirm('Are you sure you want to view the full API key? This key is sensitive.')) {
            setViewKeyId(id);
        }
    };

    const handleHide = () => setViewKeyId(null);

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            await updateApiKey(id, { status: currentStatus === 'active' ? 'inactive' : 'active' });
            toast.success('Status updated');
            fetchKeys();
        } catch (err) {
            toast.error('Failed to update status');
        }
    };

    const handleSortModelChange = (sortOption) => {
        if (isEmpty(sortOption)) {
            setSortModel(null);
            return;
        }

        const { field, sort } = first(sortOption);
        setSortModel({ field, sort });
    };

    // Filter & map rows for DataGrid
    const filteredKeys = apiKeys.filter(
        (key) =>
            key.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            key.host?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalCount = filteredKeys.length;

    // Apply client-side pagination
    const paginatedKeys = filteredKeys.slice(
        paginationModel.page * paginationModel.pageSize,
        (paginationModel.page + 1) * paginationModel.pageSize
    );

    const apirows = paginatedKeys.map((key) => ({
        id: key.id,
        name: key.name || '-',
        key:
            viewKeyId === key.id
                ? key.key
                : '*'.repeat(Math.max(0, (key.key?.length || 0) - 4)) + (key.key?.slice(-4) || ''),
        host: key.host || '-',
        created_at: formatDate(key.created_at),
        status: key.status,
        fullKey: key.key,
        originalStatus: key.status,
    }));

    // DataGrid columns
    const apicolumns = [
        { field: 'name', headerName: 'API Name', flex: 1, minWidth: 150 },
        {
            field: 'key',
            headerName: 'API Key',
            flex: 2,
            minWidth: 200,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{params.value}</span>
                    <Tooltip title={viewKeyId === params.row.id ? 'Hide' : 'View'}>
                        <IconButton
                            size="small"
                            onClick={() => (viewKeyId === params.row.id ? handleHide() : handleView(params.row.id))}
                        >
                            {viewKeyId === params.row.id ? (
                                <VisibilityOffIcon fontSize="small" />
                            ) : (
                                <VisibilityIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                </div>
            ),
        },
        { field: 'host', headerName: 'IP Address', flex: 1, minWidth: 130 },
        { field: 'created_at', headerName: 'Added Date', flex: 1, minWidth: 160 },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Switch
                        checked={params.value === 'active'}
                        onChange={() => handleToggleStatus(params.row.id, params.row.originalStatus)}
                        size="small"
                        sx={{
                            padding: 0,
                            width: 36,
                            height: 20,
                            '& .MuiSwitch-switchBase': {
                                padding: '2px',
                                '&.Mui-checked': {
                                    transform: 'translateX(16px)',
                                    color: '#fff',
                                    '& + .MuiSwitch-track': { backgroundColor: '#4C43A3', opacity: 1 },
                                },
                            },
                            '& .MuiSwitch-thumb': { width: 16, height: 16 },
                            '& .MuiSwitch-track': { borderRadius: 20, backgroundColor: '#D1D5DB', opacity: 1 },
                        }}
                    />
                    <span style={{ textTransform: 'capitalize' }}>{params.value}</span>
                </Stack>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <Tooltip title="Copy">
                        <IconButton onClick={() => handleCopy(params.row.fullKey)} color="primary">
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton onClick={() => handleDeleteKey(params.row.id)} color="error">
                            <DeleteForeverOutlined />
                        </IconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    if (loading && !apiKeys.length) {
        return (
            <div className="row justify-content-center position-relative">
                <div className="col-2 text-center">
                    <Loader isLoading={loading} />
                </div>
            </div>
        );
    }

    if (error) return <div className="alert alert-danger text-center">{error}</div>;

    return (
        <>
            <div className="custom_table_container">
                <Box variant="div" className="table_filters">
                    <h6>API Keys</h6>
                    <Box className="filter_box">
                        <TextField
                            size="small"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPaginationModel((prev) => ({ ...prev, page: 0 }));
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <span className="material-symbols-rounded">search</span>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <Button variant="contained" startIcon={<Add />} onClick={() => setShowModal(true)}>
                            Create new API key
                        </Button>
                    </Box>
                </Box>

                <Box variant="div" className="custom_table_responsive">
                    <DataGrid
                        rows={apirows}
                        columns={apicolumns}
                        getRowId={(row) => row.id}
                        autoHeight
                        disableColumnMenu
                        disableRowSelectionOnClick
                        loading={loading}
                        localeText={{ noRowsLabel: 'No API Keys Found' }}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                        slots={{
                            pagination: () => (
                                <TablePagination
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    totalCount={totalCount}
                                    pageSizeOptions={[5, 10, 25, 50]}
                                />
                            ),
                        }}
                    />
                </Box>
            </div>

            {/* Modal for creating new API key */}
            {showModal && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ background: 'rgba(0,0,0,0.3)' }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div
                            className="modal-content position-relative border-0 shadow-lg rounded-4"
                            style={{ background: 'linear-gradient(135deg, #f8fafc 60%, #e9f1fb 100%)', minWidth: 400 }}
                        >
                            <Loader isLoading={loading} />
                            <div
                                className="modal-header border-0 pb-0 d-flex flex-column align-items-center bg-white rounded-top-4"
                                style={{ borderBottom: 'none' }}
                            >
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span
                                        className="bg-primary-gradient text-white rounded-circle d-flex align-items-center justify-content-center"
                                        style={{ width: 40, height: 40, fontSize: 22 }}
                                    >
                                        <i className="bi bi-key"></i>
                                    </span>
                                    <h5 className="modal-title fw-bold mb-0" style={{ letterSpacing: 1 }}>
                                        Create API Key
                                    </h5>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close position-absolute end-0 top-0 m-3"
                                    style={{ zIndex: 2 }}
                                    onClick={() => {
                                        setShowModal(false);
                                        setFormErrors({});
                                        setApiError(null);
                                    }}
                                ></button>
                            </div>
                            <hr className="my-0" style={{ borderTop: '1.5px solid #e3e8ef', opacity: 0.7 }} />
                            <div className="modal-body py-4 px-4">
                                <label className="form-label fw-semibold text-muted">Name</label>
                                <input
                                    className={`form-control mb-2 rounded-pill px-3 py-2 fs-6${
                                        formErrors.name ? ' is-invalid' : ''
                                    }`}
                                    value={newName}
                                    onChange={(e) => {
                                        setNewName(e.target.value);
                                        setFormErrors((prev) => ({ ...prev, name: undefined }));
                                    }}
                                    placeholder="Enter key name (e.g. My API Key)"
                                    style={{ boxShadow: '0 1px 4px rgba(26,156,255,0.06)' }}
                                />
                                {formErrors.name && (
                                    <div
                                        className="invalid-feedback d-block text-danger-emphasis mb-2"
                                        style={{ fontSize: 14 }}
                                    >
                                        {formErrors.name}
                                    </div>
                                )}
                                <label className="form-label fw-semibold text-muted mt-2">Host</label>
                                <input
                                    className={`form-control mb-2 rounded-pill px-3 py-2 fs-6${
                                        formErrors.host ? ' is-invalid' : ''
                                    }`}
                                    value={newHost}
                                    onChange={(e) => {
                                        setNewHost(e.target.value);
                                        setFormErrors((prev) => ({ ...prev, host: undefined }));
                                    }}
                                    placeholder="Enter host (e.g. 192.168.1.1)"
                                    style={{ boxShadow: '0 1px 4px rgba(26,156,255,0.06)' }}
                                />
                                {formErrors.host && (
                                    <div
                                        className="invalid-feedback d-block text-danger-emphasis mb-2"
                                        style={{ fontSize: 14 }}
                                    >
                                        {formErrors.host}
                                    </div>
                                )}
                                {apiError && (
                                    <div
                                        className="alert alert-danger mt-3 rounded-3 border-0 shadow-sm py-2 px-3"
                                        style={{ fontSize: 15 }}
                                    >
                                        {apiError}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer border-0 d-flex justify-content-end px-4 py-3 bg-white rounded-bottom-4">
                                <button
                                    className="btn btn-primary-gradient rounded-pill px-4 py-2 fw-semibold text-white shadow-sm"
                                    onClick={handleAddKey}
                                    disabled={!newHost || !newName || loading}
                                    style={{ letterSpacing: 1 }}
                                >
                                    <i className="bi bi-plus-lg me-1"></i> Create
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default APIKeys;
